import React from "react";
import { useFormik } from "formik";
import * as yup from "yup";
import { useMutation } from "@tanstack/react-query";
import { useDispatch } from "react-redux";
import BaseModal from "../../../../components/ui/BaseModal";
import BaseTextField from "../../../../components/ui/BaseTextField";
import BaseButton from "../../../../components/ui/BaseButton";
import SearchLocation from "../../../../components/SearchLocation/SearchLocation";
import { createOrUpdateUserAddress } from "../../../../services/api/user";
import { showToast } from "../../../../store/toast/toastSlice";

const validationSchema = yup.object({
  addressLine1: yup.string().required("Address is required"),
  country: yup.string().required("Country is required"),
  city: yup.string().required("City is required"),
  state: yup.string().required("State is required"),
  zipCode: yup.string().notRequired(),
});

/**
 * Admin-only editor for another user's (engineer's) home address. Mirrors the
 * billing-rates admin flow: the same PATCH /users/address call, with `userId`
 * in the body so the backend updates the target engineer instead of the admin.
 */
const AdminEditAddressModal = ({ open, close, userId, personalAddress, refetchMe }) => {
  const dispatch = useDispatch();

  const { isPending, mutateAsync } = useMutation({ mutationFn: createOrUpdateUserAddress });

  const formik = useFormik({
    initialValues: {
      addressLine1: personalAddress?.addressLine1 || "",
      addressLine2: personalAddress?.addressLine2 || "",
      country: personalAddress?.country || "",
      city: personalAddress?.city || "",
      state: personalAddress?.state || "",
      zipCode: personalAddress?.zipCode || "",
      timeZone: personalAddress?.timeZone || "",
      latitude: personalAddress?.latitude || "",
      longitude: personalAddress?.longitude || "",
    },
    validationSchema,
    enableReinitialize: true,
    onSubmit: async (values) => {
      // The backend DTO rejects "" for these (IsNumber / IsNotEmpty), so send
      // them only when the address search actually provided them.
      const { latitude, longitude, timeZone, ...rest } = values;
      const payload = {
        ...rest,
        userId,
        ...(latitude !== "" && latitude != null ? { latitude: Number(latitude) } : {}),
        ...(longitude !== "" && longitude != null ? { longitude: Number(longitude) } : {}),
        ...(timeZone ? { timeZone } : {}),
      };
      try {
        await mutateAsync(payload);
        dispatch(showToast({ message: "Address updated successfully", type: "success" }));
        await refetchMe?.();
        close(false);
      } catch (error) {
        dispatch(
          showToast({
            message: error?.response?.data?.message || "Failed to update address",
            type: "error",
          }),
        );
      }
    },
  });

  const handlePlaceSelected = (selectedAddress) => {
    // SearchLocation also fires with blank values on every keystroke; only a
    // real pick from the dropdown carries the Google `place`, so apply just those.
    if (!selectedAddress?.place) return;
    formik.setValues({
      ...formik.values,
      addressLine1: selectedAddress.completeAddress || "",
      city: selectedAddress.city || "",
      country: selectedAddress.country || "",
      state: selectedAddress.state || "",
      zipCode: selectedAddress.zipCode || "",
      timeZone: selectedAddress.timeZoneName || "",
      latitude: selectedAddress.latitude || "",
      longitude: selectedAddress.longitude || "",
    });
    if (!selectedAddress.city) formik.setFieldTouched("city", true, false);
    if (!selectedAddress.state) formik.setFieldTouched("state", true, false);
  };

  const field = (name, label, optional = false) => (
    <BaseTextField
      labelShrink={false}
      label={label}
      optional={optional}
      isDefaultField={false}
      fullWidth
      id={name}
      name={name}
      value={formik.values[name]}
      onChange={formik.handleChange}
      onBlur={formik.handleBlur}
      error={formik.touched[name] && Boolean(formik.errors[name])}
      helperText={formik.touched[name] && formik.errors[name]}
    />
  );

  return (
    <BaseModal
      className="-top-[200px] overflow-auto"
      open={open}
      close={close}
      style={{ width: "1060px" }}
    >
      <div className="py-4 px-8">
        <div className="flex justify-between items-center border-b-2 pb-1">
          <h5 className="text-dark font-normal">Home Address</h5>
        </div>
      </div>

      <form className="px-8 pb-6" noValidate onSubmit={formik.handleSubmit}>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3 lg:gap-x-12 lg:gap-y-4">
          <div className="md:col-span-1 col-span-2">
            <SearchLocation onPlaceSelected={handlePlaceSelected} />
          </div>
          <div className="md:col-span-1 col-span-2">{field("addressLine1", "Address")}</div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-x-6 gap-y-3 lg:gap-x-12 mt-3">
          <div className="md:col-span-1 col-span-4">{field("country", "Country")}</div>
          <div className="md:col-span-1 col-span-4">{field("city", "City")}</div>
          <div className="md:col-span-1 col-span-4">{field("state", "State")}</div>
          <div className="md:col-span-1 col-span-4">{field("zipCode", "Zip Code", true)}</div>
        </div>

        <div className="flex justify-end gap-3 mt-6">
          <BaseButton
            type="button"
            variant="outlined"
            color="primary"
            className="px-[28px] py-[5px] bg-light text-primary hover:text-primary"
            onClick={() => close(false)}
            disabled={isPending}
          >
            Cancel
          </BaseButton>
          <BaseButton
            type="submit"
            color="primary"
            className="py-[5px] px-[38px]"
            loaderSize="22px"
            isLoading={isPending}
            disabled={!formik.dirty || isPending}
          >
            Save
          </BaseButton>
        </div>
      </form>
    </BaseModal>
  );
};

export default AdminEditAddressModal;
